import { serverError } from '@/i18n/serverErrors';
import { t } from '@/i18n';
// 🔴 ТОКЕН — ТОЛЬКО ЧЕРЕЗ `@/session` (2026-08-13). В Telegram Web игра открыта в iframe, а cookie с
// `SameSite=Lax` там не отдаётся вообще — сессия в мини-аппе не пережила бы перезапуск. Модуль пишет
// и в localStorage, и в cookie, поэтому обычный браузер ведёт себя как раньше.
import { applyAuthHeader, clearJwt, clearLegacyToken, getJwt, setJwt, setLegacyToken } from '@/session';
import { forgetTelegramOwner, rememberTelegramOwner } from '@/telegram';

// 🔴 ПРИЧИНА ОТКАЗА ВХОДА/РЕГИСТРАЦИИ — ИГРОКУ, А НЕ ТОЛЬКО В КРАСНУЮ РАМКУ (2026-08-12).
// Раньше любая ошибка гасилась в `failedLogin = true`: поля краснели, текста не было, и форма всё
// равно уходила на главную. С требованиями к паролю (волна 5.1) это стало прямой ловушкой — сервер
// отвечает «Пароль должен быть не короче 8 символов», а игрок видит просто красную рамку и не
// понимает, что не так. Серверный текст переводим тем же словарём, что и остальные ошибки.
function authError(e) {
  const detail = ((e || {}).response || {}).data;
  const text = typeof detail === 'string' ? detail : (detail || {}).detail;
  if (text) return serverError(String(text));
  return t('login.failed');
}

const state = {
  token: undefined,
  jwt: undefined,
  id: undefined,
  authorized: false,
  username: undefined,
  avatar: undefined,
  wins: undefined,
  loses: undefined,
  energy: undefined,
  telegramId: null,    // привязанный телеграм-аккаунт (кнопка привязки в профиле его прячет)
  suspicious: false,   // серый список (анти-фрод) — плашка в лобби
  blacklisted: false,  // чёрный список — плашка «награды не начисляются»
  fraudNoticeSeq: 0    // счётчик попаданий в списки: растёт на КАЖДЫЙ вход → повторная плашка
};

const getters = {};

const mutations = {
  SET_USER: (state, payload) => {
    state.id = payload.id;
    state.username = payload.username;
    state.wins = payload.wins;
    state.loses = payload.loses;
    state.energy = payload.energy;
    state.avatar = payload.avatar;
    state.jwt = payload.jwt;
    state.telegramId = payload.telegram_id || null;
    state.suspicious = !!payload.suspicious;   // анти-фрод: серый список
    state.blacklisted = !!payload.blacklisted; // анти-фрод: чёрный список
    state.fraudNoticeSeq = payload.fraud_notice_seq || 0; // номер последнего попадания в списки
    state.authorized = true;
  },
  SET_TOKEN: (state, payload) => {
    state.token = payload
  },
  SET_JWT: (state, jwt) => {
    state.jwt = jwt;
  },
  // привязали телеграм уже после входа (кнопка в профиле / авто-привязка в App.vue) — иначе стор
  // остался бы с пустым `telegramId` и профиль снова предлагал бы «Привязать Telegram»
  SET_TELEGRAM_ID: (state, id) => {
    state.telegramId = id || null;
  },
  LOGOUT: (state) => {
    state.token = undefined;
    state.jwt = undefined;
    state.id = undefined;
    state.authorized = false;
    state.username = undefined;
    state.avatar = undefined;
    state.wins = undefined;
    state.loses = undefined;
    state.energy = undefined;
    state.telegramId = null;
    state.suspicious = false;
    state.blacklisted = false;
    state.fraudNoticeSeq = 0;
  }
};

const actions = {
  TRY_AUTH: async (context, inst) => {
    await inst.$http.get(
      '/',
      {
        params: {
          token: state.token
        }
      }
    ).then((response) => {
      if(state.token){
        setLegacyToken(state.token);
      }
      setJwt(response.data.jwt);
      context.commit('SET_USER', response.data);
      context.commit('SET_CONFIGS', response.data.current_config);
      applyAuthHeader(inst.axios, response.data.jwt);
    })
  },
  LOGIN: async (context, payload) => {
    await payload.app.$http.post(
      '/login',
      {
        username: payload.payload.username,
        password: payload.payload.password,
      }
    ).then((response) => {
      setJwt(response.data.jwt);
      context.commit('CLEAR_DAILY'); // вошёл другой игрок — прогресс прежнего не показываем
      context.commit('CLEAR_TUTORIAL');
      context.commit('SET_USER', response.data);
      context.commit('SET_CONFIGS', response.data.current_config);
      applyAuthHeader(payload.app.axios, response.data.jwt);
      rememberTelegramOwner(); // вошли паролем внутри мини-аппа → сессия принадлежит этому телеграму
      payload.app.$data.formError = '';
    }
    ).catch((e) => {
        payload.app.$data.failedLogin = true
        payload.app.$data.formError = authError(e)
    })
  },
  CREATE_ACCOUNT: async (context, payload) => {
    // Реф-код из URL (?ref=CODE), пойманный при загрузке в App.vue → в query регистрации.
    // Бэк по нему привязывает новичка к пригласившему и даёт новичку стартовые билеты.
    const ref = localStorage.getItem('cz_ref_code') || undefined;
    await payload.app.$http.post(
      '/users/',
      {
        username: payload.payload.username,
        email: payload.payload.email,
        password: payload.payload.password,
      },
      ref ? { params: { ref } } : undefined
    ).then((response) => {
        localStorage.removeItem('cz_ref_code'); // использовали — больше не привязываем
        setJwt(response.data.jwt);
        context.commit('CLEAR_DAILY');
        context.commit('CLEAR_TUTORIAL');
        context.commit('SET_USER', response.data);
        context.commit('SET_CONFIGS', response.data.current_config);
        applyAuthHeader(payload.app.axios, response.data.jwt);
        rememberTelegramOwner(); // регистрация внутри мини-аппа — та же логика, что у входа
        payload.app.$data.formError = '';
      }
    ).catch((e) => {
      payload.app.$data.failedLogin = true
      payload.app.$data.formError = authError(e)
    })
  },
  SET_TOKEN: async (context, payload) => {
    context.commit('SET_TOKEN', payload);
  },
  // Продлить JWT по текущему валидному токену (POST /users/refresh) — чтобы активный игрок
  // не разлогинивался посреди партии. Возвращает true при успехе. Истёкший токен → сервер 401 → false.
  REFRESH_TOKEN: async (context, inst) => {
    const jwt = state.jwt || getJwt();
    if (!jwt) return false;
    try {
      const response = await inst.axios.post('/users/refresh', {}, {
        headers: { Authorization: `bearer ${jwt}` },
      });
      const newJwt = response.data.access_token;
      setJwt(newJwt);
      context.commit('SET_JWT', newJwt);
      applyAuthHeader(inst.axios, newJwt);
      return true;
    } catch (e) {
      return false;
    }
  },
  LOGOUT: (context, inst) => {
    clearJwt();
    clearLegacyToken();
    forgetTelegramOwner(); // сессии больше нет — следующий запуск мини-аппа решает по `initData`
    context.commit('LOGOUT');
    // Данные разделов, привязанные к ИГРОКУ: Vuex переживает выход/вход на том же устройстве, и без
    // сброса следующий игрок видел прогресс предыдущего (ловили на заданиях дня 2026-07-28).
    context.commit('CLEAR_DAILY');
    context.commit('CLEAR_TUTORIAL');
    applyAuthHeader(inst.axios, null); // иначе следующий запрос уйдёт с токеном вышедшего игрока
    // Полный разлогин обнуляет и сезонную сессию (иначе баннер/бэкап токена сезона переживут выход
    // и «протекут» в следующий вход другого пользователя на этом же устройстве/вкладке).
    context.commit('SEASON_EXIT');
    localStorage.removeItem('cz_season_main_jwt');
    localStorage.removeItem('cz_season_id');
    localStorage.removeItem('cz_season_title');
  },
}


export default {
  state,
  getters,
  mutations,
  actions,
};
